import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CalendarClock, Clock, History, Loader2, Stethoscope, Ticket } from 'lucide-react';
import RightDrawer from '@/components/RightDrawer';
import { formatDateIST } from '@/utils/date';
import VisitTimingChips from './VisitTimingChips';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: API, withCredentials: true });

/** Reception visit timeline — date, doctor, status only (no diagnosis). */
export default function ReceptionVisitHistoryDrawer({
  open, onClose, patient, excludeId, brand = '#F15A22',
}) {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !patient?.id) return undefined;
    setLoading(true);
    const params = {};
    if (excludeId) params.exclude_id = excludeId;
    ax.get(`/api/clinic-ops/patients/${patient.id}/visit-meta`, { params })
      .then(({ data }) => setVisits(data?.visits || []))
      .catch(() => setVisits([]))
      .finally(() => setLoading(false));
    return undefined;
  }, [open, patient?.id, excludeId]);

  return (
    <RightDrawer
      open={open}
      onClose={onClose}
      title={patient?.name ? `${patient.name} · visit history` : 'Visit history'}
      subtitle="Past visits at this clinic — no clinical notes or diagnosis"
      icon={<History className="h-5 w-5 text-white" />}
      accent={brand}
      data-testid="reception-visit-history"
    >
      <div className="p-5">
        {loading ? (
          <div className="py-16 text-center text-[#9AADBE]">
            <Loader2 className="h-6 w-6 mx-auto animate-spin" />
            <p className="text-sm mt-2">Loading visits…</p>
          </div>
        ) : visits.length === 0 ? (
          <div className="py-16 text-center text-[#9AADBE]">
            <CalendarClock className="h-8 w-8 mx-auto opacity-40" />
            <p className="text-sm mt-2">No prior visits on record.</p>
          </div>
        ) : (
          <ol className="relative border-l-2 border-[#D5DEE8] ml-3 space-y-0" data-testid="visit-timeline">
            {visits.map((v) => (
              <li key={v.id || `${v.date}-${v.time}-${v.appointment_number}`} className="relative pl-5 pb-5 last:pb-0">
                <span
                  className="absolute -left-[7px] top-1.5 h-3 w-3 rounded-full border-2 border-white shadow"
                  style={{ background: brand }}
                />
                <div className="rounded-xl border border-[#D5DEE8] bg-white px-3.5 py-3 shadow-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-semibold text-[#071B34]">
                      {v.date ? formatDateIST(v.date) : '—'}
                      {v.time ? ` · ${v.time}` : ''}
                    </p>
                    <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border border-[#D5DEE8] text-[#5A6B7C]">
                      {v.visit_type || 'Visit'}
                    </span>
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded border border-indigo-200 bg-indigo-50 text-indigo-800">
                      {v.status_label || v.status || '—'}
                    </span>
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-[#5A6B7C]">
                    <span className="inline-flex items-center gap-1">
                      <Stethoscope className="h-3 w-3" /> {v.doctor_name || '—'}
                    </span>
                    {v.appointment_number ? (
                      <span className="inline-flex items-center gap-1 font-mono">
                        <Ticket className="h-3 w-3" /> {v.appointment_number}
                      </span>
                    ) : null}
                    {v.queue_no ? (
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3 w-3" /> Queue #{v.queue_no}
                      </span>
                    ) : null}
                  </div>
                  <VisitTimingChips visit={v} />
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </RightDrawer>
  );
}
